import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

export const ScenarioContext = createContext();

export const ScenarioProvider = ({ children }) => {
    const [lastUpdate, setLastUpdate] = useState(Date.now());
    const [currentSituations, setCurrentSituations] = useState(() => {
        try {
            const savedSituations = localStorage.getItem('currentSituations');
            return savedSituations ? JSON.parse(savedSituations) : {};
        } catch (error) {
            console.error("Error parsing currentSituations from localStorage:", error);
            return {};
        }
    });
    const [logId, setLogId] = useState(null);

    useEffect(() => {
        try {
            localStorage.setItem('currentSituations', JSON.stringify(currentSituations));
        } catch (error) {
            console.error("Error stringifying currentSituations for localStorage:", error);
        }
    }, [currentSituations]);

    const setCurrentSituationId = useCallback((scenarioId, situationId) => {
        setCurrentSituations(prevSituations => ({
            ...prevSituations,
            [scenarioId]: situationId
        }));
    }, []);

    const startLog = useCallback(async (scenarioId, viewerId, ownerId) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/logs/start`,
                { scenarioId, viewerId, ownerId },
                { withCredentials: true }
            );
            setLogId(response.data.logId);
        } catch (error) {
            console.error("Error starting log:", error);
        }
    }, [setLogId]);

    const addDecisionToLog = useCallback(async (decision, assignmentId, fromToC) => {
        if (logId) {
            try {
                console.log("ai", assignmentId);
                await axios.patch(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/logs/${logId}/addDecision`,
                    { decision, assignmentId, fromToC }, // Send assignmentId as a sibling to decision
                    { withCredentials: true }
                );
            } catch (error) {
                console.error("Error adding decision to log:", error);
            }
        } else {
            console.warn("Attempted to add decision to log, but no logId is set.");
        }
    }, [logId]);

    const completeLog = useCallback(async () => {
        if (logId) {
            try {
                await axios.patch(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/logs/${logId}/complete`,
                    {},
                    { withCredentials: true }
                );
                setLogId(null);
            } catch (error) {
                console.error("Error completing log:", error);
            }
        } else {
            console.warn("Attempted to complete log, but no logId is set.");
        }
    }, [logId, setLogId]);

    const getSituationPriority = useCallback(async (decision, assignmentId) => {
        if(logId){
            console.log(decision);
            // try {
            //     const priorityCheck = await axios.get(
            //             `${process.env.REACT_APP_API_URL_LOCAL}/api/logs/getSituationStatus`
            //         );
            //     setLogId(null);
            // } catch (error) {
            //     console.error("Error completing log:", error);
            // }
        } else {
            console.warn("Attempted to complete log, but no logId is set.");
        }
    }, [logId, setLogId]);

    const triggerUpdate = useCallback(() => {
        setLastUpdate(Date.now());
    }, []);

    const contextValue = useMemo(() => ({
        lastUpdate,
        triggerUpdate,
        currentSituations,
        setCurrentSituationId,
        startLog,
        logId,
        setLogId,
        addDecisionToLog,
        completeLog,
        getSituationPriority,
    }), [
        lastUpdate, 
        triggerUpdate, 
        currentSituations, 
        setCurrentSituationId, 
        startLog, 
        logId, 
        setLogId,
        addDecisionToLog, 
        completeLog,
        getSituationPriority,
    ]);

    return (
        <ScenarioContext.Provider value={contextValue}>
            {children}
        </ScenarioContext.Provider>
    );
};

export const useScenario = () => {
    const context = useContext(ScenarioContext);
    if (context === undefined) {
        throw new Error('useScenario must be used within a ScenarioProvider');
    }
    return context;
};